import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLang } from '../context/LanguageContext';
import { useUser } from '../context/UserContext';
import { getLeads, createLead, updateLead, convertLeadToStudent, convertLeadToProspect, deleteLead } from '../services/api';
import EmptyState from '../components/EmptyState';

const stageOrder = [
  'interested', 'contacted', 'meeting_scheduled', 'meeting_attended',
  'applied', 'enrolled', 'active_student'
];

const stageColors: Record<string, string> = {
  interested: '#e3f2fd',
  contacted: '#fff8e1',
  meeting_scheduled: '#fce4ec',
  meeting_attended: '#e8f5e9',
  applied: '#f3e5f5',
  enrolled: '#e0f7fa',
  active_student: '#e8f5e9'
};

export default function Leads() {
  const { t, lang } = useLang();
  const { userName } = useUser();
  const [leads, setLeads] = useState<any[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [viewMode, setViewMode] = useState<'kanban' | 'table'>('kanban');
  const [form, setForm] = useState({
    firstName: '', lastName: '', firstNameAr: '', lastNameAr: '',
    phone: '', email: '', interest: '', source: 'facebook', notes: '', followUpDate: '',
    performedBy: ''
  });
  const [dragItem, setDragItem] = useState<string | null>(null);

  const loadLeads = () => {
    getLeads().then(res => setLeads(res.data)).catch(() => {});
  };

  useEffect(() => { loadLeads(); }, []);

  // Quick-add: open the new-lead modal when navigated with ?new=1
  const location = useLocation();
  useEffect(() => {
    if (new URLSearchParams(location.search).get('new') === '1') {
      setForm(f => ({ ...f, performedBy: userName }));
      setShowModal(true);
    }
  }, [location.search, userName]);

  const handleAdd = async () => {
    await createLead(form);
    setShowModal(false);
    setForm({ firstName: '', lastName: '', firstNameAr: '', lastNameAr: '', phone: '', email: '', interest: '', source: 'facebook', notes: '', followUpDate: '', performedBy: userName });
    loadLeads();
  };

  const handleDragStart = (leadId: string) => {
    setDragItem(leadId);
  };

  const handleDrop = async (stage: string) => {
    if (!dragItem) return;
    if (stage === 'active_student') {
      await convertLeadToStudent(dragItem);
    } else {
      await updateLead(dragItem, { stage });
    }
    setDragItem(null);
    loadLeads();
  };

  const handleConvert = async (id: string) => {
    if (window.confirm('להמיר ליד זה לסטודנט?')) {
      await convertLeadToStudent(id);
      loadLeads();
    }
  };

  const handleConvertToProspect = async (id: string) => {
    if (window.confirm(t('confirmConvertLeadToProspect'))) {
      await convertLeadToProspect(id);
      loadLeads();
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('האם למחוק?')) {
      await deleteLead(id);
      loadLeads();
    }
  };

  const getName = (l: any) => {
    if (lang === 'ar' && l.firstNameAr) return `${l.firstNameAr} ${l.lastNameAr}`;
    return `${l.firstName} ${l.lastName}`;
  };

  const getStageText = (stage: string) => t(`stages.${stage}`);
  const getSourceText = (source: string) => t(`sources.${source}`);

  return (
    <div style={{ width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
      <div className="page-title">
        <span className="page-title-text">🎯 {t('leadsList')}</span>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className={`btn ${viewMode === 'kanban' ? 'btn-primary' : 'btn-outline'}`} onClick={() => setViewMode('kanban')}>
            קנבן
          </button>
          <button className={`btn ${viewMode === 'table' ? 'btn-primary' : 'btn-outline'}`} onClick={() => setViewMode('table')}>
            טבלה
          </button>
          <button className="btn btn-secondary" onClick={() => { setForm({...form, performedBy: userName}); setShowModal(true); }}>+ {t('addLead')}</button>
        </div>
      </div>

      {viewMode === 'kanban' ? (
        <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
        <div className="kanban-board">
          {stageOrder.map(stage => {
            const stageLeads = leads.filter(l => l.stage === stage);
            return (
              <div
                key={stage}
                className="kanban-column"
                onDragOver={e => e.preventDefault()}
                onDrop={() => handleDrop(stage)}
              >
                <div className="kanban-column-header" style={{ background: stageColors[stage] }}>
                  <span>{getStageText(stage)}</span>
                  <span className="kanban-count">{stageLeads.length}</span>
                </div>
                {stageLeads.map(lead => (
                  <div
                    key={lead.id}
                    className="kanban-card"
                    draggable
                    onDragStart={() => handleDragStart(lead.id)}
                  >
                    <Link to={`/leads/${lead.id}`} className="kanban-card-name" style={{ color: '#7B1FA2', textDecoration: 'none', display: 'block' }}>{getName(lead)}</Link>
                    <div className="kanban-card-info" dir="ltr" style={{ textAlign: 'right' }}>📞 {lead.phone}</div>
                    <div className="kanban-card-info">📌 {lead.interest}</div>
                    <div className="kanban-card-info">🔗 {getSourceText(lead.source)}</div>
                    <div className="kanban-card-actions">
                      {stage !== 'active_student' && (
                        <>
                          <button className="btn btn-sm btn-secondary" onClick={() => handleConvertToProspect(lead.id)}>
                            📋 {t('convertToProspect')}
                          </button>
                          <button className="btn btn-sm btn-primary" onClick={() => handleConvert(lead.id)}>
                            {t('convertToStudent')}
                          </button>
                        </>
                      )}
                      <button className="btn btn-sm btn-danger" onClick={() => handleDelete(lead.id)}>✕</button>
                    </div>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
        </div>
      ) : (
        <div className="page-section">
          <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>{t('name')}</th>
                <th>{t('phone')}</th>
                <th>{t('email')}</th>
                <th>{t('interest')}</th>
                <th>{t('source')}</th>
                <th>{t('stage')}</th>
                <th>{t('followUpDate')}</th>
                <th>{t('performedBy')}</th>
                <th>{t('actions')}</th>
              </tr>
            </thead>
            <tbody>
              {leads.map(l => (
                <tr key={l.id}>
                  <td><Link to={`/leads/${l.id}`} style={{ color: '#7B1FA2', textDecoration: 'none', fontWeight: 'bold' }}>{getName(l)}</Link></td>
                  <td dir="ltr">{l.phone}</td>
                  <td dir="ltr">{l.email}</td>
                  <td>{l.interest}</td>
                  <td>{getSourceText(l.source)}</td>
                  <td><span className="badge badge-blue">{getStageText(l.stage)}</span></td>
                  <td>{l.followUpDate}</td>
                  <td>{l.performedBy || '-'}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {l.stage !== 'active_student' && (
                        <>
                          <button className="btn btn-sm btn-secondary" onClick={() => handleConvertToProspect(l.id)}>📋 {t('convertToProspect')}</button>
                          <button className="btn btn-sm btn-primary" onClick={() => handleConvert(l.id)}>{t('convertToStudent')}</button>
                        </>
                      )}
                      <button className="btn btn-sm btn-danger" onClick={() => handleDelete(l.id)}>{t('delete')}</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          {leads.length === 0 && (
            <EmptyState
              icon="🎯"
              title={t('emptyLeads')}
              actionLabel={t('addLead')}
              onAction={() => { setForm({...form, performedBy: userName}); setShowModal(true); }}
            />
          )}
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{t('addLead')}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t('firstName')} (עברית)</label>
                <input className="form-input" value={form.firstName} onChange={e => setForm({...form, firstName: e.target.value})} />
              </div>
              <div className="form-group">
                <label className="form-label">{t('lastName')} (עברית)</label>
                <input className="form-input" value={form.lastName} onChange={e => setForm({...form, lastName: e.target.value})} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t('firstName')} (ערבית)</label>
                <input className="form-input" value={form.firstNameAr} onChange={e => setForm({...form, firstNameAr: e.target.value})} />
              </div>
              <div className="form-group">
                <label className="form-label">{t('lastName')} (ערבית)</label>
                <input className="form-input" value={form.lastNameAr} onChange={e => setForm({...form, lastNameAr: e.target.value})} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t('phone')}</label>
                <input className="form-input" type="tel" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} />
              </div>
              <div className="form-group">
                <label className="form-label">{t('email')}</label>
                <input className="form-input" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t('interest')}</label>
                <input className="form-input" value={form.interest} onChange={e => setForm({...form, interest: e.target.value})} />
              </div>
              <div className="form-group">
                <label className="form-label">{t('source')}</label>
                <select className="form-select" value={form.source} onChange={e => setForm({...form, source: e.target.value})}>
                  <option value="facebook">{t('sources.facebook')}</option>
                  <option value="website">{t('sources.website')}</option>
                  <option value="referral">{t('sources.referral')}</option>
                  <option value="fair">{t('sources.fair')}</option>
                  <option value="whatsapp">{t('sources.whatsapp')}</option>
                  <option value="other">{t('sources.other')}</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">{t('followUpDate')}</label>
              <input className="form-input" type="date" value={form.followUpDate} onChange={e => setForm({...form, followUpDate: e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('notes')}</label>
              <textarea className="form-textarea" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('actionPerformer')}</label>
              <input className="form-input" value={form.performedBy} onChange={e => setForm({...form, performedBy: e.target.value})} />
            </div>
            <div className="modal-actions">
              <button className="btn btn-primary" onClick={handleAdd}>{t('save')}</button>
              <button className="btn btn-outline" onClick={() => setShowModal(false)}>{t('cancel')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
